import React, { useState } from 'react';

import { Bolt, CheckCircleOutline, ErrorOutline, QueryBuilder } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { providerQueryKeys, useProviderStatesSelectionMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AsyncButton, LightChip, Tooltip, UploadCollabAgreementModal } from '@uplift/ui-v2';

import { PracticeStateRow, PracticeStateStatus } from './PracticeStatesTable.types';

export type StateStatusCellProps = {
  value?: PracticeStateStatus;
  row: PracticeStateRow;
};

export const StateStatusCell: React.FC<StateStatusCellProps> = ({ value, row }) => {
  if (value === 'expired') {
    return <ExpiredChip expiration={row.licenseExpiration} />;
  }

  if (value === 'live')
    return (
      <LightChip
        color="success"
        label="Live"
        icon={<CheckCircleOutline fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'inReview')
    return (
      <LightChip
        color="default"
        label="In Review"
        icon={<QueryBuilder fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'getStarted') {
    return <GetStartedButton licenseId={row.id} stateId={row.stateId} />;
  }

  if (value === 'goLive') {
    return <GoLiveButton stateId={row.stateId} />;
  }

  return null;
};

type ExpiredChipProps = {
  expiration: string;
};

const ExpiredChip: React.FC<ExpiredChipProps> = ({ expiration }) => {
  const formatted = CalendarDateService.date(expiration).format(DateFormats['MMM D, YYYY']);

  return (
    <Tooltip title={`License expired on ${formatted}. Update the license expiration date to enable actions.`}>
      <span>
        <LightChip
          color="error"
          label="Expired"
          icon={<ErrorOutline fontSize="small" />}
          size="medium"
        />
      </span>
    </Tooltip>
  );
};

type GetStartedButtonProps = {
  licenseId: string;
  stateId: string;
};

const GetStartedButton: React.FC<GetStartedButtonProps> = ({ licenseId, stateId }) => {
  const [ uploadModalOpen, setUploadModalOpen ] = useState(false);
  const queryClient = useQueryClient();
  const me = useCurrentuser();

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<Bolt />}
        onClick={() => setUploadModalOpen(true)}
      >
        Get Started
      </Button>
      {uploadModalOpen && (
        <UploadCollabAgreementModal
          onClose={() => setUploadModalOpen(false)}
          licenseId={licenseId}
          stateId={stateId}
          providerId={me.id}
          onUpdateCollabAgreementSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollabAgreements(me.id) });
          }}
        />
      )}
    </>
  );
};

type GoLiveButtonProps = {
  stateId: string;
};

const GoLiveButton: React.FC<GoLiveButtonProps> = ({ stateId }) => {
  const me = useCurrentuser();
  const providerStatesSelectionMutation = useProviderStatesSelectionMutation(me?.id);

  const handleGoLive = async () => {
    const stateIds = [ stateId ];

    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: false });
    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: true });
  };


  return (
    <AsyncButton
      variant="contained"
      color="secondary"
      size="small"
      startIcon={<CheckCircleOutline />}
      onClick={handleGoLive}
    >
      Go Live
    </AsyncButton>
  );
};
